<template>
  <div>
    <!-- Quick View Drawer Modal -->
    <div v-if="booth" class="drawer-backdrop" @click="handleClose">
      <div class="drawer-panel" @click.stop>
        <!-- Drawer Header -->
        <div class="drawer-head">
          <div class="drawer-eyebrow-group">
            <span class="drawer-eyebrow">{{ mode === 'owner' ? 'GIAN HÀNG CỦA BẠN' : 'XEM NHANH GIAN HÀNG SÁCH CŨ' }}</span>
            <span class="drawer-booth-badge">{{ booth.saleType === 1 ? 'Combo ' + booth.totalBooks + ' cuốn' : booth.totalBooks + ' cuốn (Bán lẻ)' }}</span>
            <span v-if="mode === 'owner'" class="drawer-header-status" :class="'status-tag-' + booth.status">
              {{ getListingStatusLabel(booth.status) }}
            </span>
          </div>
          <button class="drawer-close-btn" @click="handleClose" title="Đóng cửa sổ">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="drawer-content">
          <!-- Main Preview Image (Stack or Selected Book Image) -->
          <figure class="drawer-figure" @click="openZoomModal(activeImage)" title="Bấm để phóng to ảnh">
            <img :src="getCoverUrl(activeImage)" :alt="booth.name" @error="handleImageError" />
            <span class="drawer-figure-zoom">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
            </span>
          </figure>

          <!-- Drawer Header Info -->
          <div class="drawer-info">
            <h2 class="drawer-book-name font-serif">{{ booth.name }}</h2>
            
            <div class="drawer-price-line">
              <div class="d-flex align-items-baseline gap-2">
                <strong class="drawer-price-val">{{ formatPrice(booth.price) }}</strong>
                <span v-if="booth.originalPrice && booth.originalPrice > booth.price" class="drawer-original-price">
                  {{ formatPrice(booth.originalPrice) }}
                </span>
                <span v-if="booth.discountRate && booth.discountRate > 0" class="drawer-sale-tag">
                  -{{ booth.discountRate }}% SALE
                </span>
              </div>
              <span class="drawer-price-note">
                {{ booth.saleType === 1 ? 'Giá trọn gói cả combo' : 'Tổng giá các cuốn (có bán lẻ)' }}
              </span>
            </div>
          </div>

          <!-- If 1 book: show condition & specs directly in drawer stats -->
          <dl class="drawer-stats" v-if="booth.books && booth.books.length === 1">
            <div class="drawer-stat">
              <dt>Độ mới</dt>
              <dd>{{ booth.books[0].perStatus }}%</dd>
            </div>
            <div class="drawer-stat">
              <dt>Tình trạng</dt>
              <dd>{{ getConditionLabel(booth.books[0].verStatus) }}</dd>
            </div>
            <div class="drawer-stat" v-if="booth.weight > 0">
              <dt>Khối lượng</dt>
              <dd>{{ formatWeight(booth.weight) }}</dd>
            </div>
            <div class="drawer-stat">
              <dt>{{ mode === 'owner' ? 'Ngày đăng' : 'Người bán' }}</dt>
              <dd>{{ mode === 'owner' ? formatDate(booth.createTime) : (booth.createByName || 'Người dùng') }}</dd>
            </div>
          </dl>

          <!-- Booth Specs Strip for combo (>= 2 books) -->
          <dl class="drawer-stats" v-else>
            <div class="drawer-stat">
              <dt>Số lượng sách</dt>
              <dd>{{ booth.totalBooks }} cuốn</dd>
            </div>
            <div class="drawer-stat">
              <dt>Hình thức bán</dt>
              <dd>{{ booth.saleType === 1 ? 'Combo bắt buộc' : 'Mua lẻ từng cuốn' }}</dd>
            </div>
            <div class="drawer-stat" v-if="booth.weight > 0">
              <dt>Khối lượng</dt>
              <dd>{{ formatWeight(booth.weight) }}</dd>
            </div>
            <div class="drawer-stat">
              <dt>{{ mode === 'owner' ? 'Ngày đăng' : 'Người bán' }}</dt>
              <dd>{{ mode === 'owner' ? formatDate(booth.createTime) : (booth.createByName || 'Người dùng') }}</dd>
            </div>
          </dl>

          <!-- List of books in booth (only when >= 2 books) -->
          <section class="drawer-books-section" v-if="booth.books && booth.books.length > 1">
            <h3 class="drawer-section-title">
              <icon icon="fa-solid fa-list-check" class="me-1 text-emerald-600" />
              Danh sách sách trong combo ({{ booth.books.length }})
            </h3>

            <div class="drawer-books-list">
              <div 
                v-for="(b, idx) in booth.books" 
                :key="b.id || idx" 
                class="drawer-book-item"
              >
                <!-- Thumbs front/back -->
                <div class="drawer-book-item-thumbs">
                  <div 
                    class="mini-thumb" 
                    :class="{ active: activeKey === 'book_' + idx + '_cover' }"
                    @click="selectBookThumb(b.cover, 'book_' + idx + '_cover')"
                    title="Bìa trước"
                  >
                    <img :src="getCoverUrl(b.cover)" alt="Bìa trước" @error="handleImageError" />
                    <span>Trước</span>
                  </div>
                  <div 
                    class="mini-thumb" 
                    :class="{ active: activeKey === 'book_' + idx + '_coverBack' }"
                    @click="selectBookThumb(b.coverBack, 'book_' + idx + '_coverBack')"
                    title="Bìa sau"
                  >
                    <img :src="getCoverUrl(b.coverBack)" alt="Bìa sau" @error="handleImageError" />
                    <span>Sau</span>
                  </div>
                </div>

                <!-- Book details -->
                <div class="drawer-book-item-info">
                  <h4 class="drawer-book-item-title">{{ idx + 1 }}. {{ b.name }}</h4>
                  <p class="drawer-book-item-author" v-if="b.author">
                    <icon icon="fa-solid fa-feather-pointed" class="me-1 opacity-75" />{{ b.author }}
                  </p>
                  <div class="drawer-book-item-badges">
                    <span class="badge-cond-pill" :class="'cond-' + b.verStatus">{{ getConditionLabel(b.verStatus) }}</span>
                    <span class="badge-per-pill">{{ b.perStatus }}% mới</span>
                    <span v-if="b.price && booth.saleType === 0" class="badge-price-pill">
                      Giá lẻ: {{ formatPrice(b.price) }}
                    </span>
                  </div>
                  <p class="drawer-book-item-desc" v-if="b.description">
                    {{ b.description }}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <!-- Drawer Action Grid -->
          <!-- MODE OWNER: Xem bài đăng & Chỉnh sửa -->
          <div v-if="mode === 'owner'" class="drawer-action-grid mt-4">
            <button class="drawer-detail-btn" @click="handleGoToDetail">
              <span>Xem bài đăng</span>
              <icon icon="fa-solid fa-arrow-up-right-from-square" class="ms-1" />
            </button>
            <button 
              class="drawer-edit-btn" 
              :disabled="!canEdit" 
              @click="handleEdit"
              :title="!canEdit ? 'Bài đăng ở trạng thái Đã bán hoặc Đã hủy không thể chỉnh sửa' : 'Chỉnh sửa bài đăng'"
            >
              <icon icon="fa-solid fa-pen-to-square" class="me-1" />
              <span>Chỉnh sửa</span>
            </button>
          </div>

          <!-- MODE PUBLIC: Thêm vào giỏ & Xem chi tiết -->
          <div v-else class="drawer-action-grid mt-4">
            <button 
              class="drawer-cart-btn" 
              :class="{ 'is-mine': isMyBooth }"
              :disabled="isMyBooth"
              @click="handleAddToCart"
            >
              <icon :icon="isMyBooth ? 'fa-solid fa-user-check' : 'fa-solid fa-cart-plus'" class="me-1" />
              <span>{{ isMyBooth ? 'Gian hàng của bạn' : 'Thêm vào giỏ' }}</span>
            </button>
            <button class="drawer-detail-btn" @click="handleGoToDetail">
              <span>Xem chi tiết</span>
              <icon icon="fa-solid fa-arrow-up-right-from-square" class="ms-1" />
            </button>
          </div>

        </div>
      </div>
    </div>

    <!-- Zoom Full Image Modal (Interactive Zoom In / Out / Pan / Wheel) -->
    <div v-if="showZoomModal" class="zoom-overlay" @click="closeZoomModal" @wheel.prevent="handleWheelZoom">
      <div class="zoom-toolbar" :class="{ 'zoom-toolbar-hidden': toolbarHidden }" @click.stop>
        <button class="zoom-tool-btn" @click="zoomOut" title="Thu nhỏ (-)" :disabled="zoomScale <= 0.5">
          <icon icon="fa-solid fa-minus" />
        </button>
        <span class="zoom-percent-badge">{{ Math.round(zoomScale * 100) }}%</span>
        <button class="zoom-tool-btn" @click="zoomIn" title="Phóng to (+)" :disabled="zoomScale >= 4">
          <icon icon="fa-solid fa-plus" />
        </button>
        <div class="zoom-tool-divider"></div>
        <button class="zoom-tool-btn" @click="resetZoom" title="Đặt lại kích thước (100%)">
          <icon icon="fa-solid fa-rotate-left" />
        </button>
        <button class="zoom-tool-btn close" @click="closeZoomModal" title="Đóng cửa sổ">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div 
        ref="zoomStage"
        class="zoom-content-stage" 
        @click.stop
        @dblclick="toggleDblZoom"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
      >
        <img 
          :src="getCoverUrl(activeImage)" 
          :alt="booth ? booth.name : ''" 
          @error="handleImageError" 
          :style="{
            transform: 'scale(' + zoomScale + ') translate(' + (zoomPanX / zoomScale) + 'px, ' + (zoomPanY / zoomScale) + 'px)',
            cursor: zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
          }"
          :class="['zoom-image-element', { 'zoom-dragging': isDragging }]"
          draggable="false"
        />
      </div>

      <div class="zoom-hint-pill" v-if="zoomScale > 1">
        <span>Cuộn chuột để Phóng to / Thu nhỏ • Giữ chuột kéo để Di chuyển</span>
      </div>
    </div>
  </div>
</template>

<script src="./OldBookQuickDrawer.js"></script>
<style scoped src="./OldBookQuickDrawer.css"></style>
